import { DIALOG_WIDTH } from '@/app/_constant/dialog-width.constants';
import { FuseConfirmDialogComponent } from '@/app/Shared/confirm-dialog/confirm-dialog.component';
import { MainAPiServiceService } from '@_services/main-api-service.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ApiAccessPopupComponent } from '@pages/system-settings/api-access-popup/api-access-popup.component';
import { ApiSecretPopupComponent } from '@pages/system-settings/api-secret-popup/api-secret-popup.component';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-apiaccess',
  templateUrl: './apiaccess.component.html',
  styleUrls: ['./apiaccess.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class APIAccessComponent implements OnInit, OnDestroy {
  private sub!: Subscription;
  applications: any[] = [];
  highlightedRows = signal<string | null>(null);
  selectedColore = signal('var(--highlighted-bg-row)');
  confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
  isLoadingResults: boolean = true;
  InvoiceValueGetData: any;
  ApiValuGetData: any;
  activeOption = 0;
  displayedColumns = [
    'APPLICATIONNAME',
    'APPLICATIONDESCRIPTION',
    'CLIENTID',
    'CREATEDATUTC',
    'ACTION',
  ];

  constructor(
    private _mainAPiServiceService: MainAPiServiceService,
    private toastr: ToastrService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.getApplications();
  }

  getApplications(): void {
    this.isLoadingResults = true;
    const payload = {
      Action: 'getapplications',
      Data: {},
    };
    this.sub = this._mainAPiServiceService.getSetData(payload, 'oauth2/apps').subscribe({
      next: (response) => {
        if (response?.CODE === 200 && response?.STATUS === 'success') {
          this.applications = response?.DATA?.RECORDS ?? [];
          this.ApiValuGetData = this.applications[0] ?? null;
          this.highlightedRows.set(this.ApiValuGetData?.APPLICATIONGUID ?? null);
          this.activeOption = this.ApiValuGetData ? 0 : -1;
        } else {
          this.applications = [];
          this.ApiValuGetData = null;
          this.highlightedRows.set(null);
          this.toastr.error(response?.MESSAGE || 'Unable to retrieve applications.');
        }
        this.isLoadingResults = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.applications = [];
        this.ApiValuGetData = null;
        this.highlightedRows.set(null);
        this.isLoadingResults = false;
        this.cdr.detectChanges();
      },
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  ApiAppsValueGet(value, index: number): void {
    this.ApiValuGetData = value;
    this.activeOption = index;
    this.highlightedRows.set(value?.APPLICATIONGUID ?? null);
  }
  /**
   * Opens the API access dialog for creating or editing an API application.
   *
   * @param type Operation type used by the API access popup.
   */

  newApiAccessPopup(type: 'new' | 'edit', application?: any): void {
    const dialogRef = this.dialog.open(ApiAccessPopupComponent, {
      disableClose: true,
      data: {
        Type: type,
        Application: type === 'edit' ? application : null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.success) {
        return;
      }

      // Refresh the list
      this.getApplications();
      if (result.isNew || result.regenerated) {
        setTimeout(() => {
          this.dialog.open(ApiSecretPopupComponent, {
            disableClose: true,
            data: {
              secret: result.secret,
            },
          });
        });
      }
    });
  }

  /**
   * delete a particular invoice from list
   */
  deleteApiAccessPopup(application: any): void {
    this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
      disableClose: true,
      width: DIALOG_WIDTH.FULL,
    });
    this.confirmDialogRef.componentInstance.confirmMessage = 'Are you sure you want to delete?';
    this.confirmDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isLoadingResults = true;
        this._mainAPiServiceService
          .getSetData(
            {
              Action: 'deleteapplication',
              Data: { APPLICATIONGUID: application.APPLICATIONGUID },
            },
            'oauth2/apps',
          )
          .subscribe((response) => {
            if (response.CODE == 200 && response.STATUS == 'success') {
              this.toastr.success('Deleted successfully');
              this.getApplications();
            } else {
              this.toastr.error(response.MESSAGE);
            }
          });
      }
    });
  }

  /**
   * Masks the API key with asterisks for secure display.
   *
   * @param apiKey API key to mask.
   * @returns A masked API key or "-" when no API key is available.
   */
  maskApiKey(apiKey: string | null | undefined): string {
    if (!apiKey) {
      return '-';
    }
    return '*'.repeat(apiKey.length);
  }

  /**
   * Copies the API key to the clipboard and displays a success notification.
   *
   * The API key is copied in its original form even when it is masked
   * in the user interface.
   */
  copyApiKey(): void {
    const apiKey = this.ApiValuGetData.APIGATEWAYAPIKEY;
    if (!apiKey) {
      return;
    }
    navigator.clipboard.writeText(apiKey).then(() => {
      this.toastr.success('API Key copied successfully');
    });
  }
}
